import { useMutation, useQuery } from "@tanstack/react-query";

import UploadSiteConfiguration from "./UploadSiteConfiguration/UploadSiteConfiguration";
import {
  configureSiteMutation,
  getSiteIoConfigurationQueryKey,
} from "../../../../services/client/@tanstack/react-query.gen";
import SiteConfigurationDiagram from "./SiteConfigurationDiagram/SiteConfigurationDiagram";
import { useNotify } from "../../../util-components/Notifications/notifications";
import { Button, Space } from "antd";
import { useMemo, useState } from "react";
import {
  SiteConfigurationInput,
  SiteConfigurationOutputReadable,
  SiteIoConfiguration,
} from "../../../../services/client";
import { getSiteIoConfiguration } from "../../../../services/client/sdk.gen";
import { SiteConfigDiffModal } from "./SiteConfigDiffModal";
import {
  computeSiteConfigDiff,
  diffChangeCount,
} from "../utils/siteConfigDiffUtils";
import {
  formatAssetNames,
  phaseRotationDeclarations,
} from "../../../../util/phaseRotations";
import { DisableForViewer } from "../../../common/DisableForViewer";

type SiteConfigurationTabProps = {
  site_configuration?: SiteConfigurationOutputReadable | null;
  refetch_site: () => void;
  site_id: string;
};
export const SiteConfigurationTab: React.FC<SiteConfigurationTabProps> = ({
  site_configuration,
  site_id,
  refetch_site,
}) => {
  const [edit, setEdit] = useState(!site_configuration);
  const [pendingData, setPendingData] = useState<
    SiteConfigurationInput | undefined
  >(undefined);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const notify = useNotify();

  const configureSite = useMutation({
    ...configureSiteMutation(),
    onError: (_: Error) =>
      notify({
        type: "error",
        message: "Something went wrong! Please try again.",
      }),
    onSuccess: () => {
      setPendingData(undefined);
      setConfirmOpen(false);
      setEdit(false);
      refetch_site();
    },
  });

  const diff = useMemo(
    () => computeSiteConfigDiff(site_configuration, pendingData),
    [site_configuration, pendingData],
  );
  const hasChanges = pendingData !== undefined && diffChangeCount(diff) > 0;

  const { data: siteIoConfiguration } = useQuery<SiteIoConfiguration | null>({
    queryKey: getSiteIoConfigurationQueryKey({ path: { site_id } }),
    queryFn: async ({ signal }) => {
      const response = await getSiteIoConfiguration({
        path: { site_id },
        signal,
      });
      if (response.response.status === 404) {
        return null;
      }
      if (response.error) {
        throw response.error;
      }
      return response.data ?? null;
    },
  });

  // Saving a configuration with undeclared rotations while the site IO
  // configuration runs in use_topology silently degrades the whole site to
  // worst-case phase behaviour: the controller only uses declared rotations
  // when every asset declares one.
  const rotationWarning = useMemo(() => {
    if (siteIoConfiguration?.phase_permutation_mode !== "use_topology") {
      return undefined;
    }
    const { undeclared } = phaseRotationDeclarations(
      pendingData,
      siteIoConfiguration,
    );
    if (undeclared.length === 0) {
      return undefined;
    }
    return `This site's IO configuration is set to use topology, but ${formatAssetNames(
      undeclared,
    )} declare${
      undeclared.length === 1 ? "s" : ""
    } no phase rotation. The controller only uses declared rotations when every collector and device declares one, so saving this configuration makes it fall back to worst-case phase behaviour for the whole site. Declare a rotation (identity counts) on every asset to keep phase-aware control.`;
  }, [siteIoConfiguration, pendingData]);

  const handleSave = () => {
    if (!pendingData) {
      return;
    }
    configureSite.mutate({
      body: { site_configuration: pendingData },
      path: { site_id },
    });
  };

  const handleToggleEdit = () => {
    if (edit) {
      setPendingData(undefined);
    }
    setEdit(!edit);
  };

  return (
    <div className="w-full h-full relative flex justify-center">
      <Space className="absolute top-5 right-5 z-50">
        {(edit || !site_configuration) && (
          <>
            <Button
              disabled={!hasChanges || configureSite.isPending}
              onClick={() => setPreviewOpen(true)}
            >
              Preview
            </Button>
            <Button
              type="primary"
              disabled={!hasChanges}
              loading={configureSite.isPending}
              onClick={() => setConfirmOpen(true)}
            >
              Save
            </Button>
          </>
        )}
        {site_configuration && (
          <DisableForViewer>
            <Button
              type={edit ? "default" : "primary"}
              disabled={configureSite.isPending}
              onClick={handleToggleEdit}
            >
              {edit ? "Cancel" : "Edit"}
            </Button>
          </DisableForViewer>
        )}
      </Space>
      {edit || !site_configuration ? (
        <div className="w-full md:w-3/5 xl:w-2/5 h-full">
          <UploadSiteConfiguration
            initialData={site_configuration}
            onChange={setPendingData}
            site_id={site_id}
          />
        </div>
      ) : (
        <SiteConfigurationDiagram
          site_configuration={site_configuration}
          site_id={site_id}
        />
      )}

      <SiteConfigDiffModal
        open={previewOpen}
        diff={diff}
        mode="preview"
        onCancel={() => setPreviewOpen(false)}
      />
      <SiteConfigDiffModal
        open={confirmOpen}
        diff={diff}
        mode="save"
        onCancel={() => setConfirmOpen(false)}
        onOk={handleSave}
        confirmLoading={configureSite.isPending}
        warning={rotationWarning}
      />
    </div>
  );
};
